import Cards from 'react-credit-cards';
import { Form, Formik } from 'formik';
import { InputField } from 'components/Formik/InputField';
import { DateInputField } from 'components/Formik/DateInputField';
import { Col, Row } from 'components/Grid';
import 'react-credit-cards/es/styles-compiled.css';
import { textRegular } from 'styles/typography';
import { ButtonGroup } from 'components/deprecated/Button/ButtonGroup';
import { cx } from '@linaria/core';
import { flex } from 'styles/utils';
import { Button } from 'components/deprecated/Button';
import { useCallback, useMemo, useState } from 'react';
import { formatNumberType } from 'modules/User/utils/creditCard';
import { object, string } from 'yup';
import { regularFormGroupEl } from 'components/Formik/Forms.styles';
import { isDateInPast } from 'utils/validation';
import { cardContainer, cardInfoWrapper } from './CreditCardModal.styles';
type CardFields = {
  'card-name': string;
  'card-number': string;
  'card-expiry': string;
  'card-cvc': string;
  address: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
};
export type CreditCardFormSubmitCallback = {
  number: string;
  exp_month: string;
  exp_year: string;
  cvc: string;
  name: string;
  address_line_1: string;
  address_line_2: string;
  address_zip: string;
  address_country: string;
  address_city: string;
  address_state: string;
  id: string;
};
type CreditCardModalProps = {
  onClose: () => void;
  onSubmit: (card: CreditCardFormSubmitCallback) => void;
  cardId?: string; // this can be used to determine edit mode
  initialValues?: CardFields;
};
const defaultValues = {
  'card-name': '',
  'card-number': '',
  'card-expiry': '',
  'card-cvc': '',
  address: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  country: ''
};
const requiredStringRules = string().required('Required');
const CreditCardSchema = object().shape({
  'card-name': requiredStringRules,
  'card-number': requiredStringRules,
  'card-cvc': string().required('Required').min(3, 'Please enter correct CVV'),
  'card-expiry': string().required('Required').min(5, 'Please enter correct date'),
  address: requiredStringRules,
  city: requiredStringRules,
  zip: requiredStringRules,
  country: requiredStringRules
});
type cardExpiryErrorType = {
  'card-expiry'?: string;
};
export const CreditCardModal: React.FC<CreditCardModalProps> = ({
  onClose,
  onSubmit,
  cardId = '',
  initialValues = defaultValues
}) => {
  const isEditMode = useMemo(() => Boolean(cardId), [cardId]);
  const [focus, setFocus] = useState<'number' | 'cvc' | 'expiry' | 'name' | undefined>(undefined);
  const handleInputFocus = useCallback(e => {
    setFocus(e.target.id); // required for the card flip animation
  }, []);
  const validate = (values: CardFields): cardExpiryErrorType => {
    const errors: {
      [key: string]: string;
    } = {};
    const isValidDate = isDateInPast(values['card-expiry'])();
    if (isValidDate) {
      errors['card-expiry'] = isValidDate;
    }
    return errors;
  };
  return <div className={cardContainer}>
      <Formik enableReinitialize initialValues={initialValues} validationSchema={CreditCardSchema} validate={validate} onSubmit={(values: CardFields) => {
      onSubmit({
        id: cardId,
        name: values['card-name'],
        number: values['card-number'],
        exp_month: values['card-expiry']?.split('/')[0],
        exp_year: values['card-expiry']?.split('/')[1],
        cvc: values['card-cvc'],
        address_line_1: values.address,
        address_line_2: values.address2,
        address_city: values.city,
        address_state: values.state,
        address_zip: values.zip,
        address_country: values.country
      });
    }}>
        {({
        isSubmitting,
        isValid,
        dirty,
        values,
        setFieldValue
      }) => <Form>
            <div className={cardInfoWrapper}>
              <div data-hiddenvalue>
                <Cards cvc={values ? values['card-cvc'] : ''} expiry={values ? values['card-expiry'] : ''} name={values ? values['card-name'] : ''} number={values ? values['card-number'] : ''} focused={focus} />
              </div>
              <div className="card-inputs fullWidth">
                <div className={regularFormGroupEl}>
                  <InputField id="name" name="card-name" label="" placeholder="Name on card" onFocus={handleInputFocus} data-hiddenvalue />
                </div>
                <div className={regularFormGroupEl}>
                  <InputField name="card-number" label="" placeholder="Card Number" id="number" onChange={event => {
                setFieldValue('card-number', formatNumberType(event.target.value));
              }} onFocus={handleInputFocus} disabled={isEditMode} maxLength={16} data-hiddenvalue />
                </div>
                <Row columnSpacing={1}>
                  <Col xs={12} md={8} className={regularFormGroupEl}>
                    <DateInputField id="date" label="" name="card-expiry" placeholder="MM/YY" onFocus={handleInputFocus} disabled={isEditMode} data-hiddenvalue />
                  </Col>
                  <Col xs={12} md={4} className={regularFormGroupEl}>
                    <InputField name="card-cvc" label="" placeholder="CVV" id="cvc" onChange={event => {
                  setFieldValue('card-cvc', formatNumberType(event.target.value));
                }} disabled={isEditMode} onFocus={handleInputFocus} maxLength={4} data-hiddenvalue />
                  </Col>
                </Row>
              </div>
            </div>

            <Row spacing={1}>
              <Col xs={12}>
                <h3 className={textRegular}>Billing Details</h3>
              </Col>
              <Col xs={12} md={6}>
                <InputField name="address" label="Address Line 1" placeholder="Address Line 1" data-hiddenvalue />
              </Col>
              <Col xs={12} md={6}>
                <InputField name="address2" label="Address Line 2" placeholder="Address Line 2" data-hiddenvalue />
              </Col>
              <Col xs={12} md={6}>
                <InputField name="city" label="City" placeholder="City" data-hiddenvalue />
              </Col>
              <Col xs={12} md={6}>
                <InputField name="state" label="State or Province" placeholder="State or Province" data-hiddenvalue />
              </Col>
              <Col xs={12} md={6}>
                <InputField name="zip" label="Zip Code/ Postal Code" placeholder="Zip Code/ Postal Code" data-hiddenvalue />
              </Col>
              <Col xs={12} md={6}>
                <InputField name="country" label="Country or Region" placeholder="Country or Region" data-hiddenvalue />
              </Col>
              <Col xs={12}>
                <ButtonGroup className={cx(flex.init, flex.justifyContent.end, 'buttons')}>
                  <Button kind="outline" type="button" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button kind="primary" type="submit" disabled={!isValid || !dirty || isSubmitting} isLoading={isSubmitting}>
                    Confirm
                  </Button>
                </ButtonGroup>
              </Col>
            </Row>
          </Form>}
      </Formik>
    </div>;
};